<template>
  <!-- F1: 최초 로그인 비밀번호 강제 변경 화면 — SPEC-CHINA-ACCTSEC-001 -->
  <div class="force-pw-container">
    <div class="force-pw-card">
      <div class="force-pw-logo">
        <img src="../../assets/img/certilogo.png" alt="logo" />
      </div>

      <h3 class="force-pw-title">{{ $t('force_pw_title') }}</h3>
      <p class="force-pw-desc">{{ $t('force_pw_desc') }}</p>

      <a-alert
        v-if="errorMsg"
        type="error"
        :message="errorMsg"
        style="margin-bottom: 16px;"
        show-icon
        closable
        @close="errorMsg = ''"
      />

      <a-form layout="vertical" :model="form" @finish="onSubmit">
        <!-- 현재 비밀번호 -->
        <a-form-item
          :label="$t('force_pw_current_label')"
          name="currentPassword"
          :rules="[{ required: true, message: $t('force_pw_current_required') }]"
        >
          <a-input-password
            v-model:value="form.currentPassword"
            :placeholder="$t('force_pw_current_placeholder')"
            autocomplete="current-password"
          />
          <div class="form-hint">{{ $t('force_pw_current_hint') }}</div>
        </a-form-item>

        <!-- 새 비밀번호 -->
        <a-form-item
          :label="$t('force_pw_new_label')"
          name="newPassword"
          :rules="newPasswordRules"
        >
          <a-input-password
            v-model:value="form.newPassword"
            :placeholder="$t('force_pw_new_placeholder')"
            autocomplete="new-password"
          />
        </a-form-item>

        <!-- 새 비밀번호 확인 -->
        <a-form-item
          :label="$t('force_pw_confirm_label')"
          name="confirmPassword"
          :rules="confirmPasswordRules"
        >
          <a-input-password
            v-model:value="form.confirmPassword"
            :placeholder="$t('force_pw_confirm_placeholder')"
            autocomplete="new-password"
          />
        </a-form-item>

        <a-button
          type="primary"
          html-type="submit"
          :loading="submitting"
          block
          style="margin-top: 8px;"
        >
          {{ $t('force_pw_submit') }}
        </a-button>
      </a-form>

      <!-- 다음에 변경하기 / 로그아웃 버튼 -->
      <div style="text-align: center; margin-top: 16px;">
        <a-button type="link" @click="onSkip">{{ $t('force_pw_skip') }}</a-button>
        <a-button type="link" @click="onLogout">{{ $t('force_pw_logout') }}</a-button>
      </div>
    </div>
  </div>
</template>

<script>
// @MX:NOTE: [AUTO] SPEC-CHINA-ACCTSEC-001 F1 — 최초 로그인 비밀번호 강제 변경 화면
//   pwChangeRequiredManu flag 존재 시 router.beforeEach가 이 화면으로 리다이렉트.
//   성공 시 flag 제거 후 앱 기본 페이지로 이동 (재로그인 불필요 — BE tokenVersion 무변경).
import { resolveLandingPath } from '@/utils/landing';

export default {
  name: 'ForcePasswordChange',

  data() {
    return {
      form: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      },
      submitting: false,
      errorMsg: '',
    };
  },

  computed: {
    newPasswordRules() {
      return [
        { required: true, message: this.$t('force_pw_new_required') },
        { min: 8, message: this.$t('force_pw_new_min_length') },
        {
          validator: (_, value) => {
            if (!value || !this.form.currentPassword) return Promise.resolve();
            if (value === this.form.currentPassword) {
              return Promise.reject(new Error(this.$t('force_pw_new_same_as_current')));
            }
            return Promise.resolve();
          },
        },
      ];
    },

    confirmPasswordRules() {
      return [
        { required: true, message: this.$t('force_pw_confirm_required') },
        {
          validator: (_, value) => {
            if (!value || !this.form.newPassword) return Promise.resolve();
            if (value !== this.form.newPassword) {
              return Promise.reject(new Error(this.$t('force_pw_confirm_mismatch')));
            }
            return Promise.resolve();
          },
        },
      ];
    },
  },

  methods: {
    /**
     * 앱 진입 공통 처리 — 일반 로그인(signInMixin)과 동일하게 회사정보/권한을 먼저 로드한 뒤
     * 권한 첫 메뉴로 이동한다.
     *
     * 강제변경 게이트로 진입한 시점에는 signInMixin이 getUserInfo 호출 전 return 하므로
     * store.userInfo 가 비어 있다. getPrivileges 는 username 누락 시 null 을 반환하여
     * 사이드바 메뉴가 비고 product-list 로 fallback 되는 버그가 있었다.
     * → getCompanyInfo 로 userInfo(userId=username) 를 먼저 채운 뒤 getPrivileges 호출.
     */
    /**
     * 앱 진입 공통 처리 — 회사정보/권한 로드 후 권한 첫 메뉴(없으면 /no-menu)로 이동.
     *
     * 강제변경 게이트 진입 시점에는 signInMixin이 getUserInfo 전 return하므로
     * store.userInfo가 비어 있음. getCompanyInfo로 userInfo 먼저 채운 뒤 getPrivileges 호출.
     */
    async goToAppHome() {
      try {
        await this.$store.dispatch('getCompanyInfo');
      } catch (e) { /* noop: 회사정보 실패해도 진입 시도 */ }

      const username = this.$store.state.userInfo?.userId || '';
      // resolveLandingPath: 첫 메뉴 링크 있으면 해당 경로, 없으면 /no-menu (product-list fallback 제거)
      let priv = null;
      try {
        priv = await this.$store.dispatch('getPrivileges', { username, force: true });
      } catch (e) { /* noop */ }
      this.$router.push(resolveLandingPath(priv));
    },

    async onSubmit() {
      this.submitting = true;
      this.errorMsg = '';
      try {
        await this.$store.dispatch('changeCompanyPassword', {
          currentPassword: this.form.currentPassword,
          newPassword: this.form.newPassword,
        });
        // 성공: 강제변경 게이트 해제 (현재 토큰 그대로 유효)
        localStorage.removeItem('pwChangeRequiredManu');
        sessionStorage.removeItem('pwChangeSkippedManu');
        this.$message.success(this.$t('force_pw_success'));
        // 일반 로그인과 동일하게 권한 로드 후 첫 메뉴로 진입 (새로고침 불필요)
        await this.goToAppHome();
      } catch (err) {
        const msg = err?.response?.data?.message;
        this.errorMsg = msg || this.$t('force_pw_error_generic');
      } finally {
        this.submitting = false;
      }
    },

    /**
     * 다음에 변경하기 — 비밀번호 변경을 미룬다.
     * 이번 세션에 한해 강제 게이트를 통과(sessionStorage skip flag)하되,
     * pwChangeRequiredManu(localStorage)는 유지하여 다음 로그인 시 다시 강제 변경 폼이 노출된다.
     * (signInMixin 이 로그인마다 skip flag 를 제거하므로 비번 변경 전까지 매 로그인 폼 재출현)
     */
    onSkip() {
      sessionStorage.setItem('pwChangeSkippedManu', 'Y');
      this.goToAppHome();
    },

    onLogout() {
      this.$store.dispatch('clearLocalStorage');
      localStorage.removeItem('pwChangeRequiredManu');
      sessionStorage.removeItem('pwChangeSkippedManu');
      this.$router.push('/signin');
    },
  },
};
</script>

<style scoped>
.force-pw-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  padding: 24px;
}

.force-pw-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  padding: 40px 36px;
  width: 100%;
  max-width: 420px;
}

.force-pw-logo {
  text-align: center;
  margin-bottom: 24px;
}

.force-pw-logo img {
  height: 36px;
  object-fit: contain;
}

.force-pw-title {
  text-align: center;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #1a1a1a;
}

.force-pw-desc {
  text-align: center;
  color: #666;
  font-size: 14px;
  margin-bottom: 24px;
  line-height: 1.5;
}

.form-hint {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}
</style>
